import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const inp = { width: "100%", background: "#0A0A0A", border: "1px solid #333", borderRadius: 8, padding: "9px 11px", color: "#fff", fontSize: 14, outline: "none" };
const lab = { display: "block", color: "#A0A0A0", fontSize: 10, fontWeight: 800, letterSpacing: 1.2, marginBottom: 4 };
const FIELDS = [
  ["signed_date", "Signed date", "date"], ["start_date", "Start date *", "date"], ["term_years", "Term (years)", "number"],
  ["renewal_date", "Renewal / end date", "date"], ["renewal_notice_days", "Renewal notice (days)", "number"], ["royalty_percent", "Royalty %", "number"],
  ["marketing_percent", "Marketing fund %", "number"], ["tech_fee_usd", "Technology fee (USD / month)", "number"], ["min_general_liability_usd", "Min. general liability (USD)", "number"],
];
const TEXTS = [["territory", "Territory"], ["insurance_requirements", "Insurance requirements"], ["other_requirements", "Other requirements (training, reporting…)"]];
const NUMERIC = FIELDS.filter((f) => f[2] === "number").map((f) => f[0]);

export default function ReviewTermsDialog({ row, onClose, onSaved }) {
  const ag = row.agreement;
  const [f, setF] = useState(() => Object.fromEntries([...FIELDS, ...TEXTS].map(([k]) => [k, ag.terms?.[k] ?? ""])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.start_date) return toast.error("Enter the start date.");
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : NUMERIC.includes(k) ? Number(v) : String(v).trim()]));
      await api(`/agreements/admin/${row.user_id}/terms`, { method: "PUT", body });
      toast.success("Agreement terms confirmed"); onSaved(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[1001] max-h-[88vh] overflow-y-auto border-[#333] bg-[#121212] text-white sm:max-w-2xl" data-testid="agreement-review-dialog">
        <DialogHeader>
          <DialogTitle>Confirm agreement terms · {row.full_name}</DialogTitle>
          <DialogDescription className="text-neutral-400">AI filled these in from {ag.filename}. Check each one against the PDF and correct anything wrong. Nothing counts until you confirm.</DialogDescription>
        </DialogHeader>
        {ag.extracted?.notes && <p data-testid="agreement-ai-notes" className="rounded-lg border border-[#F5B700] bg-[#F5B700]/10 p-3 text-xs text-[#F5B700]">AI note: {ag.extracted.notes}</p>}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {FIELDS.map(([k, l, type]) => (
            <label key={k}><span style={lab}>{l.toUpperCase()}</span><input data-testid={`agreement-${k}`} type={type} step="any" style={inp} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
          ))}
        </div>
        {TEXTS.map(([k, l]) => (
          <label key={k}><span style={lab}>{l.toUpperCase()}</span><textarea data-testid={`agreement-${k}`} rows={2} style={inp} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></label>
        ))}
        <p className="text-[11px] text-neutral-500">Portal standard: 10% royalty, 1% marketing fund, $330/month technology fee. Differences are flagged for admins only.</p>
        <button onClick={save} disabled={busy} data-testid="agreement-confirm-btn" className="w-full rounded-[10px] bg-[#4CCC06] py-3 text-sm font-extrabold text-black">{busy ? <Loader2 className="mx-auto animate-spin" size={18} /> : "Confirm terms"}</button>
      </DialogContent>
    </Dialog>
  );
}
